import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useThemeClasses } from "@/hooks/useThemeClasses";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Plus, Search, Edit, Trash2, Tag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const ITEMS_PER_PAGE = 15;

const Acessorios = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const lojaSlug = slug || user?.loja_slug;
  const { isDark, card, input, textPrimary, textSecondary, textMuted, tableRow, button } = useThemeClasses();
  const [acessorios, setAcessorios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { 
    if (lojaSlug) {
      setLoading(true);
      fetchData(); 
    }
  }, [lojaSlug, location.key]);
  
  useEffect(() => { setCurrentPage(1); }, [search, categoriaFilter]);

  const fetchData = async () => {
    try {
      const [acessoriosRes, categoriasRes] = await Promise.all([
        axios.get(`${API}/loja/${lojaSlug}/acessorios`),
        axios.get(`${API}/loja/${lojaSlug}/acessorios/categorias`)
      ]);
      setAcessorios(acessoriosRes.data);
      setCategorias(categoriasRes.data.categorias || []);
    } catch (error) {
      toast.error("Erro ao carregar acessórios");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/loja/${lojaSlug}/acessorios/${deleteId}`);
      toast.success("Acessório excluído com sucesso");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir acessório");
    } finally {
      setDeleteId(null);
    }
  };

  const formatCurrency = (value) => 
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const filteredAcessorios = acessorios.filter(a => {
    const matchSearch = a.nome.toLowerCase().includes(search.toLowerCase()) ||
                        (a.descricao || "").toLowerCase().includes(search.toLowerCase()) ||
                        (a.codigo_barras || "").includes(search);
    const matchCategoria = categoriaFilter === "all" || a.categoria === categoriaFilter;
    return matchSearch && matchCategoria;
  });

  const totalPages = Math.ceil(filteredAcessorios.length / ITEMS_PER_PAGE);
  const paginatedAcessorios = filteredAcessorios.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalEstoque = acessorios.reduce((sum, a) => sum + (a.quantidade || 0), 0);
  const valorEstoque = acessorios.reduce((sum, a) => sum + ((a.quantidade || 0) * (a.preco || 0)), 0);
  const estoqueBaixo = acessorios.filter(a => (a.quantidade || 0) <= 5).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#D4AF37]">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="acessorios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold font-['Outfit'] ${textPrimary}`}>Acessórios</h1>
          <p className={`text-sm ${textMuted}`}>Gerencie capinhas, carregadores, fones e outros</p>
        </div>
        <Link to={`/${lojaSlug}/acessorios/novo`}>
          <Button className="bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F]" data-testid="btn-novo-acessorio">
            <Plus className="w-4 h-4 mr-2" />Novo Acessório
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className={card}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${textMuted}`}>Itens em Estoque</p>
                <p className={`text-xl font-bold ${textPrimary}`}>{totalEstoque}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={card}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <Tag className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${textMuted}`}>Valor em Estoque</p>
                <p className="text-xl font-bold text-[#D4AF37]">{formatCurrency(valorEstoque)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={card}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className={`text-xs uppercase tracking-wider ${textMuted}`}>Estoque Baixo</p>
                <p className="text-xl font-bold text-orange-500">{estoqueBaixo} itens</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-4 h-4" />
          <Input 
            placeholder="Buscar por nome, descrição ou código..." 
            className={`pl-10 ${input}`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-acessorio"
          />
        </div>
        <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
          <SelectTrigger className={`w-full sm:w-[200px] ${input}`}>
            <SelectValue placeholder="Todas categorias" />
          </SelectTrigger>
          <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : 'bg-white border-gray-200'}>
            <SelectItem value="all">Todas categorias</SelectItem>
            {categorias.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className={card}>
        <CardContent className="p-0">
          {paginatedAcessorios.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className={`${isDark ? 'border-white/5' : 'border-gray-100'} hover:bg-transparent`}>
                      <TableHead className={`uppercase text-xs whitespace-nowrap ${textMuted}`}>Nome</TableHead>
                      <TableHead className={`uppercase text-xs whitespace-nowrap ${textMuted}`}>Categoria</TableHead>
                      <TableHead className={`uppercase text-xs whitespace-nowrap ${textMuted}`}>Preço</TableHead>
                      <TableHead className={`uppercase text-xs whitespace-nowrap ${textMuted}`}>Estoque</TableHead>
                      <TableHead className={`uppercase text-xs text-right whitespace-nowrap ${textMuted}`}>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAcessorios.map((acessorio) => (
                      <TableRow key={acessorio.id} className={tableRow}>
                        <TableCell className="whitespace-nowrap">
                          <div>
                            <span className={`font-medium ${textPrimary}`}>{acessorio.nome}</span>
                            {acessorio.descricao && (
                              <p className={`text-xs truncate max-w-[200px] ${textMuted}`}>{acessorio.descricao}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs font-semibold rounded">
                            {acessorio.categoria}
                          </span>
                        </TableCell>
                        <TableCell className="text-[#D4AF37] font-semibold whitespace-nowrap">
                          {formatCurrency(acessorio.preco)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className={`font-medium ${acessorio.quantidade <= 5 ? 'text-orange-500' : 'text-green-500'}`}>
                            {acessorio.quantidade || 0}
                          </span>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <Link to={`/${lojaSlug}/acessorios/editar/${acessorio.id}`}>
                              <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-400 hover:bg-blue-400/10">
                                <Edit className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-gray-400 hover:text-red-400 hover:bg-red-400/10"
                              onClick={() => setDeleteId(acessorio.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredAcessorios.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCurrentPage}
              />
            </>
          ) : (
            <div className="p-12 text-center">
              <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className={textMuted}>Nenhum acessório encontrado</p>
              <Link to={`/${lojaSlug}/acessorios/novo`}>
                <Button className="mt-4 bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F]">
                  Cadastrar Acessório
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : 'bg-white border-gray-200'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={textPrimary}>Excluir Acessório</AlertDialogTitle>
            <AlertDialogDescription className={textMuted}>
              Tem certeza que deseja excluir este acessório? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? 'bg-transparent border-white/10 text-white hover:bg-white/5' : 'bg-gray-100 border-gray-200 text-gray-700'}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 text-white hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Acessorios;
